import { Stack, Typography, TypographyProps } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import CalendarIcon from "assets/icons/CalendarIcon";
import getInputVariantSx from "components/inputs/inputStyles";
import dayjs from "dayjs";
import { FC } from "react";
import { useController, UseControllerProps } from "react-hook-form";

export type DatePickerProps = {
  name: string;
  label?: string;
  rules?: UseControllerProps["rules"];
  defaultValue?: string;
  disabled?: boolean;
  minDate?: Date;
  maxDate?: Date;
  validateIfIsTouched?: boolean;
  variant?: "outlined";
  hint?: string;
  labelProps?: TypographyProps;
};

const DatePickerControl: FC<DatePickerProps> = ({
  name,
  label,
  rules,
  defaultValue,
  disabled,
  minDate,
  maxDate,
  validateIfIsTouched = false,
  variant,
  hint,
  labelProps,
}) => {
  const { sx: labelSx, ...restLabelProps } = labelProps || {};

  const {
    field,
    fieldState: { error, isDirty },
  } = useController({
    name,
    defaultValue,
    rules: {
      ...rules,
      validate: (value, formValues, ...args) => {
        if (!value) return value;
        const dateValue = dayjs(value);

        if (!dateValue.isValid()) return "Неверный формат даты";

        if (validateIfIsTouched && !isDirty) return true;

        if (dateValue.isBefore("1900-01-01")) return "Дата не может быть раньше 01.01.1900";

        if (maxDate && dateValue.isAfter(dayjs(maxDate).endOf("day"))) {
          return `Нельзя выбрать дату позже ${dayjs(maxDate).format("DD.MM.YYYY")}`;
        }
        if (minDate && dateValue.isBefore(dayjs(minDate).startOf("day"))) {
          return `Нельзя выбрать дату раньше ${dayjs(minDate).format("DD.MM.YYYY")}`;
        }

        const externalValidate = rules?.validate;
        if (!externalValidate) return dateValue.toDate();

        if (typeof externalValidate === "function") {
          return externalValidate(value, formValues, ...args);
        }

        const validateList = Object.values(externalValidate);
        for (let i = 0; i < validateList.length; i += 1) {
          const validateResult = validateList[i](value, formValues, ...args);
          if (validateResult !== true) return validateResult;
        }

        return true;
      },
    },
  });

  return (
    <Stack spacing={0.5}>
      <Typography
        variant='body14rg'
        sx={{ color: "base.500", ...labelSx }}
        {...restLabelProps}>
        {label}
      </Typography>
      <DatePicker
        minDate={minDate ? dayjs(minDate) : undefined}
        maxDate={maxDate ? dayjs(maxDate) : undefined}
        value={field.value ? dayjs(field.value) : null}
        onChange={field.onChange}
        views={["day", "month", "year"]}
        format='DD.MM.YYYY'
        disabled={disabled}
        slots={{
          openPickerIcon: CalendarIcon,
        }}
        slotProps={{
          textField: {
            fullWidth: true,
            error: !!error,
            helperText: error?.message || hint,
            variant,
            sx: getInputVariantSx(variant),
          },
          openPickerButton: {
            sx: { color: "base.200" },
          },
          openPickerIcon: {
            sx: { width: 16, height: 16 },
          },
        }}
      />
    </Stack>
  );
};

export default DatePickerControl;
